import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  Column,
  FormBox,
  InputText,
  Notice,
  PageActions,
  Row,
  Select,
  SelectLoading,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import HelpVideoButton from "@components/HelpVideoButton";
import { usePageHeader } from "@components/ow";
import { bankDataReturnPath, storeBankData } from "@helpers/bankData";
import { FormPage, FormPageSkeleton, FullWidth, focusFirstError } from "../../Common/FormPage";

// Dados bancários do prestador (modelo Formulário do DS OneWork). A empresa paga
// pela chave PIX ou pela conta: o back aceita uma das duas, e a conta só completa.

const BREADCRUMB = [{ page: "Dados bancários", route: "/e/bank" }];

const EMPTY = { pixKey: "", bankCode: "", bankName: "", bankAgency: "", bankAccount: "", bankAccountDigit: "" };

const ACCOUNT_FIELDS = [
  { key: "bankCode", id: "bank-bank", message: "Escolha o banco." },
  { key: "bankAgency", id: "bank-agency", message: "Informe a agência." },
  { key: "bankAccount", id: "bank-account", message: "Informe a conta." },
  { key: "bankAccountDigit", id: "bank-digit", message: "Informe o dígito." },
];

const trimmed = (values) => Object.fromEntries(Object.entries(values).map(([key, value]) => [key, String(value || "").trim()]));

const bankLabel = (name, code) => (code ? `${name} (${code})` : name);

/** Erros por id do campo, na ordem da tela: PIX ou conta, e a conta começada precisa estar completa. */
export function validateBank(values) {
  const v = trimmed(values);
  const errors = {};
  const accountStarted = ACCOUNT_FIELDS.some(({ key }) => v[key]);
  if (!v.pixKey && !accountStarted) {
    errors["bank-pix"] = "Informe a chave PIX ou a conta.";
    return errors;
  }
  if (accountStarted) {
    ACCOUNT_FIELDS.forEach(({ key, id, message }) => {
      if (!v[key]) errors[id] = message;
    });
  }
  return errors;
}

/** Corpo que a API recebe, igual ao da tela antiga: sem conta, os campos dela vão vazios. */
function bankPayload(values) {
  const v = trimmed(values);
  const hasAccount = !!v.bankCode;
  return {
    pixKey: v.pixKey,
    bankName: hasAccount ? v.bankName : "",
    bankCode: hasAccount ? v.bankCode : "",
    bankAgency: hasAccount ? v.bankAgency : "",
    bankAccount: hasAccount ? v.bankAccount : "",
    bankAccountDigit: hasAccount ? v.bankAccountDigit : "",
    bankAccountType: "Corrente",
  };
}

function fromApi(bankData) {
  return {
    pixKey: bankData?.pix_key || "",
    bankCode: bankData?.bank_code ? String(bankData.bank_code) : "",
    bankName: bankData?.bank_name || "",
    bankAgency: bankData?.bank_agency || "",
    bankAccount: bankData?.bank_account || "",
    bankAccountDigit: bankData?.bank_account_digit || "",
  };
}

export default function BankEmployee() {
  const userService = useService("users");
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const [saved, setSaved] = useState(EMPTY);
  const [values, setValues] = useState(EMPTY);
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);
  const [banksFailed, setBanksFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Os erros aparecem nos campos depois da primeira tentativa e se atualizam a cada mudança
  const [submitted, setSubmitted] = useState(false);

  usePageHeader({ title: "Dados bancários", breadcrumb: BREADCRUMB });

  useEffect(() => {
    // A lista de bancos vem da BrasilAPI, de fora: o formulário não espera por ela
    loadBankData().finally(() => setLoading(false));
    loadBanks().finally(() => setBanksLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadBankData() {
    try {
      const { data } = await userService.get(user.id);
      const current = fromApi(data?.bankData);
      setSaved(current);
      setValues(current);
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível carregar seus dados bancários");
    }
  }

  async function loadBanks() {
    const data = await userService.getBankNames();
    if (!Array.isArray(data)) {
      setBanksFailed(true);
      return;
    }
    setBanks(data.filter((bank) => bank.code));
  }

  // O banco salvo entra na lista mesmo que a BrasilAPI não o traga (ou esteja fora), para não sumir do campo
  const bankOptions = useMemo(() => {
    const options = banks.map((bank) => ({ value: String(bank.code), label: bankLabel(bank.name, bank.code), name: bank.name }));
    if (saved.bankCode && !options.some((option) => option.value === saved.bankCode)) {
      options.unshift({ value: saved.bankCode, label: bankLabel(saved.bankName, saved.bankCode), name: saved.bankName });
    }
    return options;
  }, [banks, saved.bankCode, saved.bankName]);

  const fieldErrors = submitted ? validateBank(values) : {};
  const dirty = JSON.stringify(trimmed(values)) !== JSON.stringify(trimmed(saved));
  const registered = !!(saved.pixKey || saved.bankCode);

  const change = (field) => (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }));

  function discard() {
    setValues(saved);
    setSubmitted(false);
  }

  async function handleSubmit() {
    setSubmitted(true);
    const errors = validateBank(values);
    if (Object.keys(errors).length) {
      focusFirstError(Object.keys(errors));
      return;
    }

    setSaving(true);
    const payload = bankPayload(values);
    // O service mostra o toast do resultado (sucesso ou a mensagem do back)
    const result = await userService.updateBankData(user.id, payload);
    setSaving(false);
    if (!result || result.error) return;

    const current = {
      pixKey: payload.pixKey,
      bankCode: payload.bankCode,
      bankName: payload.bankName,
      bankAgency: payload.bankAgency,
      bankAccount: payload.bankAccount,
      bankAccountDigit: payload.bankAccountDigit,
    };
    storeBankData({
      pix_key: current.pixKey,
      bank_name: current.bankName,
      bank_code: current.bankCode,
      bank_agency: current.bankAgency,
      bank_account: current.bankAccount,
      bank_account_digit: current.bankAccountDigit,
      bank_account_type: payload.bankAccountType,
    });
    setSaved(current);
    setValues(current);
    setSubmitted(false);

    const back = bankDataReturnPath(location.search);
    if (back) navigate(back);
  }

  if (loading) return <FormPageSkeleton sections={[150, 190]} />;

  return (
    <FormPage
      notice={
        <>
          <PageActions>
            <HelpVideoButton videoKey="employeeBank" />
          </PageActions>
          {!registered ? (
            <Notice $variant="blue" $text="Cadastre a chave PIX ou a conta para enviar notas fiscais." />
          ) : null}
        </>
      }
      actions={
        <PageActions>
          {dirty ? <ButtonLink $text="Descartar alterações" $action={discard} /> : null}
          <Button
            $text="Salvar dados bancários"
            $variant="primary"
            $action={handleSubmit}
            $isLoading={saving}
            $isDisabled={!dirty}
          />
        </PageActions>
      }
    >
      <FormBox title="Chave PIX">
        <Row $gap={20}>
          <Column $columns={6}>
            <InputText
              $idForm="bank-pix"
              $label="Chave PIX"
              $variant="default"
              $hasTooltip="CPF, CNPJ, e-mail, telefone ou chave aleatória."
              $errors={fieldErrors["bank-pix"]}
              value={values.pixKey}
              onChange={change("pixKey")}
            />
          </Column>
        </Row>
      </FormBox>

      <FormBox title="Conta bancária">
        <FullWidth>
          <Text $text="Preencha a chave PIX, a conta ou as duas." $variant="caption_medium" $color="N400" />
        </FullWidth>
        {banksFailed ? (
          <FullWidth>
            <Notice $variant="orange" $text="Não foi possível carregar a lista de bancos. Tente de novo mais tarde." />
          </FullWidth>
        ) : null}
        <Row $gap={20}>
          <Column $columns={5}>
            {banksLoading ? (
              <SelectLoading $idForm="bank-bank" $label="Banco" $errors={fieldErrors["bank-bank"]} placeholder="Carregando bancos" />
            ) : (
              <Select
                $idForm="bank-bank"
                $label="Banco"
                $isClearable
                $maxMenuHeight={240}
                $options={bankOptions}
                $errors={fieldErrors["bank-bank"]}
                placeholder="Busque pelo nome ou pelo código"
                $textNoOptions="Nenhum banco encontrado"
                value={bankOptions.find((option) => option.value === values.bankCode) || null}
                onChange={(option) =>
                  setValues((prev) => ({ ...prev, bankCode: option ? option.value : "", bankName: option ? option.name : "" }))
                }
              />
            )}
          </Column>
          <Column $columns={2}>
            <InputText
              $idForm="bank-agency"
              $label="Agência"
              $variant="default"
              $errors={fieldErrors["bank-agency"]}
              value={values.bankAgency}
              onChange={change("bankAgency")}
            />
          </Column>
          <Column $columns={3}>
            <InputText
              $idForm="bank-account"
              $label="Conta"
              $variant="default"
              $errors={fieldErrors["bank-account"]}
              value={values.bankAccount}
              onChange={change("bankAccount")}
            />
          </Column>
          <Column $columns={2}>
            <InputText
              $idForm="bank-digit"
              $label="Dígito"
              $variant="default"
              $errors={fieldErrors["bank-digit"]}
              value={values.bankAccountDigit}
              onChange={change("bankAccountDigit")}
            />
          </Column>
        </Row>
      </FormBox>
    </FormPage>
  );
}
